import { elementNarrationEnd, elementNarrationStart, pickRenderSet } from './layout';
import { activeNarratorCueAtPlayhead } from './narratorSync';

/** Narration being spoken at playhead (may span multiple overlapping windows). */
export function narrationActiveAt(elements, t) {
  const parts = [];
  for (const el of elements || []) {
    const start = elementNarrationStart(el);
    const end = elementNarrationEnd(el);
    const narr = (el.narration || '').trim();
    if (start <= t && t < end && narr) parts.push(narr);
  }
  return parts.join(' ').slice(0, 500);
}

/** Layer lifecycle state for one element at time t. */
export function layerState(el, t, fps = 30) {
  const start = elementNarrationStart(el);
  const end = Number(el.end) || elementNarrationEnd(el);
  const dt = 1 / Math.max(fps, 1);
  if (t < start) return 'hidden';
  if (start <= t && t < start + dt * 1.5) return 'entering';
  if (end - dt * 1.5 <= t && t < end) return 'exiting';
  return 'visible';
}

/**
 * Visual plan at playhead — uses stored frame_manifest when present, else derives from timeline.
 */
export function framePlanAtPlayhead(rendered, playhead) {
  if (!rendered) return null;
  const fps = Number(rendered.fps) || 30;
  const frameIdx = Math.floor(playhead * fps);
  const manifest = rendered.frame_manifest;
  if (manifest?.frames?.length) {
    let best = manifest.frames[0];
    for (const fr of manifest.frames) {
      if ((fr.frame_index ?? 0) <= frameIdx) best = fr;
      else break;
    }
    return {
      source: 'manifest',
      time: best.time,
      frame_index: best.frame_index,
      scene_id: best.scene_id,
      narration_active: best.narration_active,
      layers: best.layers || [],
      code_panel: best.code_panel || {},
      entering: best.entering_layer_ids || [],
      exiting: best.exiting_layer_ids || [],
    };
  }

  const { draw, codeLines, highlightLine, scene } = pickRenderSet(
    rendered.elements || [],
    rendered.scenes || [],
    playhead,
    rendered,
  );
  const layers = draw.map((el) => {
    const age = playhead - (Number(el.start) || 0);
    let opacity = 1;
    if (el.type === 'scene_title' && age > 4) opacity = 0.55;
    else if (['explanation', 'bullet'].includes(el.type) && age > 10) opacity = 0.88;
    return {
      id: el.id,
      type: el.type,
      content: el.content,
      state: layerState(el, playhead, fps),
      opacity,
      stack_index: el.layout?.stack_index,
    };
  });

  const narratorCue = activeNarratorCueAtPlayhead(rendered, playhead);

  return {
    source: 'derived',
    time: playhead,
    frame_index: frameIdx,
    scene_id: scene?.id,
    narration_active: narrationActiveAt(rendered.elements, playhead),
    narrator_cue: narratorCue,
    concise_text: narratorCue?.concise_text || null,
    layers,
    code_panel: { lines: codeLines, highlight_line: highlightLine },
    entering: layers.filter((l) => l.state === 'entering').map((l) => l.id),
    exiting: layers.filter((l) => l.state === 'exiting').map((l) => l.id),
  };
}
